/**
 * The chrome every pane on the Wire & Funding tab shares, in three bands:
 *
 *   PaneHeader — what this artifact is (title + tags), one line on why, actions
 *   PaneStrip  — the pane's headline numbers, as labelled cells on a tinted rule
 *   body       — the ledger / form
 *
 * The strip is the piece that makes the four panes read as one workspace: each
 * one answers "where does this stand?" in the same place, in the same shape,
 * before you reach its detail. It also means a closer can compare two panes by
 * glancing at the same band rather than hunting for the equivalent figure.
 *
 * Deliberately NOT `PrimaryContentCard` — that wraps itself in a Container,
 * forces full width below 1600px and carries a 32px pad plus a 32px bottom
 * margin, all of which fight a rail + pane layout.
 */
import PropTypes from "prop-types";

import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";

import Flx from "../../../../../components/layout/Flx";
import Txt from "../../../../../components/typography/Txt";
import { T, labelSx } from "../wireFundingShared";

export const PaneHeader = ({
  title,
  badges,
  subtitle,
  actions,
  // Funding Shield puts a three-cell status bar in the actions slot; letting it
  // drop under the title doubles the header height and pushes the card content
  // off screen, so that pane opts out and lets the subtitle truncate instead.
  wrap = true,
}) => (
  <Flx
    jb
    wrap={wrap}
    gap={2}
    sx={{
      px: 3,
      pt: 2.5,
      pb: 2,
      alignItems: "flex-start",
      flexWrap: wrap ? "wrap" : "nowrap",
    }}
  >
    <Flx column gap={0.6} sx={{ minWidth: 0 }}>
      <Flx ac wrap gap={1}>
        <Txt
          sx={{
            fontSize: 17,
            fontWeight: 700,
            letterSpacing: "-0.01em",
            color: T.ink,
          }}
        >
          {title}
        </Txt>
        {badges}
      </Flx>
      {subtitle ? (
        <Txt
          sx={{
            fontSize: 12.5,
            color: T.muted,
            lineHeight: 1.5,
            maxWidth: "66ch",
          }}
        >
          {subtitle}
        </Txt>
      ) : null}
    </Flx>
    {actions ? (
      <Flx ac gap={1} sx={{ flex: "none" }}>
        {actions}
      </Flx>
    ) : null}
  </Flx>
);

PaneHeader.propTypes = {
  wrap: PropTypes.bool,
  title: PropTypes.string,
  badges: PropTypes.node,
  subtitle: PropTypes.node,
  actions: PropTypes.node,
};

/**
 * One cell in the strip. `tone` colours the value only — the label stays neutral
 * so a red figure reads as the number being wrong, not the field being wrong.
 */
export const StripCell = ({ label, value, sub, tone, children }) => (
  <Flx
    column
    gap={0.4}
    sx={{
      px: 2,
      py: 1.5,
      minWidth: 170,
      "& + &": { borderLeft: `1px solid ${T.line2}` },
    }}
  >
    <Txt sx={{ ...labelSx, display: "block" }}>{label}</Txt>
    {children || (
      <Txt
        sx={{
          fontSize: 14,
          fontWeight: 600,
          color: tone || T.ink,
          fontFamily: typeof value === "string" && /[$\d]/.test(value)
            ? T.mono
            : undefined,
          letterSpacing: "-0.01em",
        }}
      >
        {value}
      </Txt>
    )}
    {sub ? (
      <Txt sx={{ fontSize: 11, color: T.faint, lineHeight: 1.3 }}>{sub}</Txt>
    ) : null}
  </Flx>
);

/** The band of cells under a pane header, with optional right-aligned meta. */
export const PaneStrip = ({ children, meta }) => (
  <Flx
    wrap
    sx={{
      alignItems: "stretch",
      borderTop: `1px solid ${T.line2}`,
      borderBottom: `1px solid ${T.line2}`,
      bgcolor: T.subtle,
      px: 1,
      flex: "none",
    }}
  >
    {children}
    <Box sx={{ flex: 1 }} />
    {meta ? (
      <Flx
        ac
        sx={{ px: 2, py: 1.5, maxWidth: 340, justifyContent: "flex-end" }}
      >
        <Txt
          sx={{
            fontSize: 11.5,
            color: T.faint,
            textAlign: "right",
            lineHeight: 1.4,
          }}
        >
          {meta}
        </Txt>
      </Flx>
    ) : null}
  </Flx>
);

export const PaneShell = ({ header, strip, children, sx, bodySx }) => (
  <Flx
    column
    sx={{
      bgcolor: T.card,
      border: `1px solid ${T.line}`,
      borderRadius: "14px",
      boxShadow: T.shadow,
      minWidth: 0,
      // A flex column defaults to min-content height, so a tall child can push
      // the card past whatever height a caller sets. `minHeight: 0` is what lets
      // a fixed-height pane actually hold its size and scroll internally.
      minHeight: 0,
      ...sx,
    }}
  >
    {header}
    {strip}
    <Box sx={{ px: 3, py: 2.5, minWidth: 0, ...bodySx }}>{children}</Box>
  </Flx>
);

PaneShell.propTypes = {
  header: PropTypes.node,
  strip: PropTypes.node,
  children: PropTypes.node,
  sx: PropTypes.object,
  bodySx: PropTypes.object,
};

export const PaneLoading = () => (
  <Flx center sx={{ py: 8 }}>
    <CircularProgress size={26} />
  </Flx>
);

export const PaneEmpty = ({ children }) => (
  <Txt
    sx={{
      fontSize: 13,
      color: T.muted,
      py: 4,
      maxWidth: 560,
      lineHeight: 1.6,
      "& code": {
        fontFamily: T.mono,
        fontSize: 12,
        bgcolor: T.line2,
        px: 0.5,
        borderRadius: "4px",
      },
    }}
  >
    {children}
  </Txt>
);
